import { useState, useCallback, useRef } from 'react';

/**
 * Easter Egg Hook: Detects when user clicks/taps on an element multiple times
 * 
 * Works on both desktop (clicks) and mobile (taps).
 * 
 * Usage:
 * ```tsx
 * const { handleClick, handleTouchEnd, clickCount } = useDevModeEasterEgg(onActivate);
 * 
 * <div onClick={handleClick} onTouchEnd={handleTouchEnd}>
 *   Logo here
 * </div>
 * ```
 * 
 * @param onActivate - Callback fired when easter egg is triggered
 * @param requiredClicks - Number of clicks required (default: 8)
 * @param resetTimeMs - Time window to complete the sequence (default: 3000ms)
 */
export function useDevModeEasterEgg(
  onActivate: () => void,
  requiredClicks: number = 8,
  resetTimeMs: number = 3000
): {
  handleClick: () => void;
  handleTouchEnd: (e: React.TouchEvent) => void;
  clickCount: number;
} {
  const [clickCount, setClickCount] = useState(0);
  const resetTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivationRef = useRef<number>(0);
  const lastTapRef = useRef<number>(0); // Track last tap to prevent double-firing

  // Reset the counter
  const resetCounter = useCallback(() => {
    setClickCount(0);
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  }, []);

  // Core increment logic (shared by click and touch)
  const incrementCounter = useCallback(() => {
    // Prevent triggering more than once per second (debounce rapid activations)
    const now = Date.now();
    if (now - lastActivationRef.current < 1000) return;

    // Increment counter
    const newCount = clickCount + 1;
    setClickCount(newCount);

    // Start/reset the timeout
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
    }
    resetTimeoutRef.current = setTimeout(resetCounter, resetTimeMs);

    // Check if sequence complete
    if (newCount >= requiredClicks) {
      lastActivationRef.current = now;
      resetCounter();
      onActivate();
    }
  }, [clickCount, requiredClicks, resetTimeMs, resetCounter, onActivate]);

  // Handle click (desktop)
  const handleClick = useCallback(() => {
    // Skip if this was triggered by a recent touch event (prevents double-firing)
    const now = Date.now();
    if (now - lastTapRef.current < 500) return;
    incrementCounter();
  }, [incrementCounter]);

  // Handle touch end (mobile) - fires before onClick on touch devices
  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    e.preventDefault(); // Prevent subsequent click event
    lastTapRef.current = Date.now();
    incrementCounter();
  }, [incrementCounter]);

  return {
    handleClick,
    handleTouchEnd,
    clickCount,
  };
}

